import { QueryString } from '~/utils/index.js';
import { del, get, patch, post, put } from './agent.js';

const url = '/admin';
const administratorsUrl = `${url}/admins`;
const aiReplayUrl = `${url}/ai/replay`;
const aiDashboardUrl = `${url}/ai/dashboard`;
const traceReplayUrl = `${url}/trace/replay`;
const entityReplayUrl = `${url}/entity/replay`;
const notificationReplayUrl = `${url}/notification/replay`;
const demoWorkspacesUrl = `${url}/demo-workspaces`;
const emailsUrl = `${url}/emails`;
const membersUrl = `${url}/members`;
const productUpdatesUrl = `${url}/product-updates`;
const feedbackItemsUrl = `${url}/feedback-items`;
const reportsUrl = `${url}/reports`;
const userUrl = `${url}/user`;
const workspaceRequestsUrl = `${url}/workspace-requests`;
const workspacesUrl = `${url}/workspaces`;
const xeroUrl = `${url}/xero`;

export default {
  login: (credentials) => post(`${url}/login`, credentials),
  logout: (credentials) => post(`${url}/logout`, credentials),
  session: () => get(`${url}/session`),
  administrators: {
    get: () => get(administratorsUrl),
    create: (body) => post(administratorsUrl, body),
    update: (adminId, body) => patch(`${administratorsUrl}/${adminId}`, body),
    delete: (adminId) => del(`${administratorsUrl}/${adminId}`),
  },
  aiReplay: {
    list: (params = {}) => get(aiReplayUrl.concat(new QueryString(params).toString(true))),
    getTrace: (conversationId) => get(`${aiReplayUrl}/${conversationId}`),
    resolve: (params = {}) => get(aiReplayUrl.concat('/resolve').concat(new QueryString(params).toString(true))),
    recentWorkspaces: (params = {}) =>
      get(aiReplayUrl.concat('/recent/workspaces').concat(new QueryString(params).toString(true))),
    recentMembers: (params = {}) =>
      get(aiReplayUrl.concat('/recent/members').concat(new QueryString(params).toString(true))),
    searchWorkspaces: (params = {}) =>
      get(aiReplayUrl.concat('/search/workspaces').concat(new QueryString(params).toString(true))),
    searchMembers: (params = {}) =>
      get(aiReplayUrl.concat('/search/members').concat(new QueryString(params).toString(true))),
  },
  traceReplay: {
    list: (params = {}) => get(traceReplayUrl.concat(new QueryString(params).toString(true))),
    getTrace: (traceId) => get(`${traceReplayUrl}/${traceId}`),
    resolve: (params = {}) => get(traceReplayUrl.concat('/resolve').concat(new QueryString(params).toString(true))),
    recentWorkspaces: (params = {}) =>
      get(traceReplayUrl.concat('/recent/workspaces').concat(new QueryString(params).toString(true))),
    recentMembers: (params = {}) =>
      get(traceReplayUrl.concat('/recent/members').concat(new QueryString(params).toString(true))),
    searchWorkspaces: (params = {}) =>
      get(traceReplayUrl.concat('/search/workspaces').concat(new QueryString(params).toString(true))),
    searchMembers: (params = {}) =>
      get(traceReplayUrl.concat('/search/members').concat(new QueryString(params).toString(true))),
  },
  entityReplay: {
    list: (params = {}) => get(entityReplayUrl.concat(new QueryString(params).toString(true))),
    getEntity: (entity, entityId) => get(`${entityReplayUrl}/${entity}/${entityId}`),
  },
  notificationReplay: {
    list: (params = {}) => get(notificationReplayUrl.concat(new QueryString(params).toString(true))),
    getNotification: (notificationId) => get(`${notificationReplayUrl}/${notificationId}`),
    resolve: (params = {}) =>
      get(notificationReplayUrl.concat('/resolve').concat(new QueryString(params).toString(true))),
    recentWorkspaces: (params = {}) =>
      get(notificationReplayUrl.concat('/recent/workspaces').concat(new QueryString(params).toString(true))),
    searchWorkspaces: (params = {}) =>
      get(notificationReplayUrl.concat('/search/workspaces').concat(new QueryString(params).toString(true))),
  },
  aiDashboard: {
    getSummary: (params = {}) => get(aiDashboardUrl.concat('/summary').concat(new QueryString(params).toString(true))),
    getConcentration: (params = {}) =>
      get(aiDashboardUrl.concat('/concentration').concat(new QueryString(params).toString(true))),
    getByWorkspace: (params = {}) =>
      get(aiDashboardUrl.concat('/by-workspace').concat(new QueryString(params).toString(true))),
    getByModel: (params = {}) => get(aiDashboardUrl.concat('/by-model').concat(new QueryString(params).toString(true))),
    getOverTime: (params = {}) =>
      get(aiDashboardUrl.concat('/over-time').concat(new QueryString(params).toString(true))),
    getToolCalls: (params = {}) =>
      get(aiDashboardUrl.concat('/tool-calls').concat(new QueryString(params).toString(true))),
    getMembers: (params = {}) => get(aiDashboardUrl.concat('/members').concat(new QueryString(params).toString(true))),
    getConversationDepth: (params = {}) =>
      get(aiDashboardUrl.concat('/conversation-depth').concat(new QueryString(params).toString(true))),
    getActivityOverTime: (params = {}) =>
      get(aiDashboardUrl.concat('/activity-over-time').concat(new QueryString(params).toString(true))),
    getFeedback: (params = {}) =>
      get(aiDashboardUrl.concat('/feedback').concat(new QueryString(params).toString(true))),
    getFeedbackOverTime: (params = {}) =>
      get(aiDashboardUrl.concat('/feedback-over-time').concat(new QueryString(params).toString(true))),
    getMissedMessages: (params = {}) =>
      get(aiDashboardUrl.concat('/missed-messages').concat(new QueryString(params).toString(true))),
  },
  user: {
    setupMfa: () => post(`${userUrl}/mfa`),
    verifyMfa: (code) => post(`${userUrl}/mfa/verify`, { code }),
    changePassword: (body) => post(`${userUrl}/change-password`, body),
  },
  demoWorkspaces: {
    get: () => get(demoWorkspacesUrl),
    create: (body) => post(demoWorkspacesUrl, body),
    delete: (workspaceId) => del(`${demoWorkspacesUrl}/${workspaceId}`),
  },
  workspaceRequests: {
    get: (params = {}) => get(workspaceRequestsUrl.concat(new QueryString(params).toString(true))),
    invite: (requestId) => post(`${workspaceRequestsUrl}/${requestId}/invite`),
    reject: (requestId) => post(`${workspaceRequestsUrl}/${requestId}/reject`),
  },
  workspaces: {
    get: (params = {}) => get(workspacesUrl.concat(new QueryString(params).toString(true))),
    getMembers: (workspaceId) => get(`${workspacesUrl}/${workspaceId}/members`),
    getGeneral: (workspaceId) => get(`${workspacesUrl}/${workspaceId}/general`),
    getActivity: (workspaceId) => get(`${workspacesUrl}/${workspaceId}/activity`),
    clone: ({ workspaceId, workspaceName, workspaceKey }) =>
      post(`${workspacesUrl}/${workspaceId}/clone`, { workspaceName, workspaceKey }),
    changeStatus: (workspaceId, statusId) => patch(`${workspacesUrl}/${workspaceId}/status`, { statusId }),
    setTrial: (workspaceId, trialEndsAt) => patch(`${workspacesUrl}/${workspaceId}/trial`, { trialEndsAt }),
    updateNotes: (workspaceId, body) => patch(`${workspacesUrl}/${workspaceId}/notes`, body),
    updatePreviews: (workspaceId, previews) => patch(`${workspacesUrl}/${workspaceId}/previews`, { previews }),
    createDemoData: (workspaceId) => post(`${workspacesUrl}/${workspaceId}/create-demo-data`),
    subscribe: (workspaceId) => post(`${workspacesUrl}/${workspaceId}/subscribe`),
  },
  members: {
    get: (params = {}) => get(membersUrl.concat(new QueryString(params).toString(true))),
    getByIds: (memberIds) => get(`${membersUrl}/select?memberIds=${memberIds.join(',')}`),
    getById: (memberId) => get(`${membersUrl}/${memberId}`),
    getWorkspaces: (memberId) => get(`${membersUrl}/${memberId}/workspaces`),
    impersonate: (memberId) => post(`${membersUrl}/impersonation`, { memberId }),
    stopImpersonation: () => del(`${membersUrl}/impersonation`),
    changeStatus: (memberId, statusId) => patch(`${membersUrl}/${memberId}/status`, { statusId }),
  },
  emails: {
    get: (params = {}) => get(emailsUrl.concat(new QueryString(params).toString(true))),
    sync: () => post(`${emailsUrl}/sync`),
    remove: (suppressionId) => del(`${emailsUrl}/${suppressionId}`),
  },
  reports: {
    getDailyActiveUsers: (params = {}) =>
      get(reportsUrl.concat('/daily-active-users').concat(new QueryString(params).toString(true))),
    getHourlyActiveUsers: (params = {}) =>
      get(reportsUrl.concat('/hourly-active-users').concat(new QueryString(params).toString(true))),
    getInvoicesPerWorkspace: (params = {}) =>
      get(reportsUrl.concat('/invoices-per-workspace').concat(new QueryString(params).toString(true))),
    getMembersPerWorkspace: (params = {}) =>
      get(reportsUrl.concat('/members-per-workspace').concat(new QueryString(params).toString(true))),
    getMonthlyActiveUsers: (params = {}) =>
      get(reportsUrl.concat('/monthly-active-users').concat(new QueryString(params).toString(true))),
    getRatings: (params = {}) => get(reportsUrl.concat('/ratings').concat(new QueryString(params).toString(true))),
    getTrackedHoursPerDay: (params = {}) =>
      get(reportsUrl.concat('/tracked-hours-per-day').concat(new QueryString(params).toString(true))),
    getTrackedHoursPerWorkspace: (params = {}) =>
      get(reportsUrl.concat('/tracked-hours-per-workspace').concat(new QueryString(params).toString(true))),
    getTrialsEndingSoon: (params = {}) =>
      get(reportsUrl.concat('/trials-ending-soon').concat(new QueryString(params).toString(true))),
    getWeeklyActiveUsers: (params = {}) =>
      get(reportsUrl.concat('/weekly-active-users').concat(new QueryString(params).toString(true))),
  },
  reportCSVs: {
    getDailyActiveUsers: (params = {}) =>
      get(reportsUrl.concat('/daily-active-users/csv').concat(new QueryString(params).toString(true))),
    getHourlyActiveUsers: (params = {}) =>
      get(reportsUrl.concat('/hourly-active-users/csv').concat(new QueryString(params).toString(true))),
    getInvoicesPerWorkspace: (params = {}) =>
      get(reportsUrl.concat('/invoices-per-workspace/csv').concat(new QueryString(params).toString(true))),
    getMembersPerWorkspace: (params = {}) =>
      get(reportsUrl.concat('/members-per-workspace/csv').concat(new QueryString(params).toString(true))),
    getMonthlyActiveUsers: (params = {}) =>
      get(reportsUrl.concat('/monthly-active-users/csv').concat(new QueryString(params).toString(true))),
    getRatings: (params = {}) => get(reportsUrl.concat('/ratings/csv').concat(new QueryString(params).toString(true))),
    getTrackedHoursPerDay: (params = {}) =>
      get(reportsUrl.concat('/tracked-hours-per-day/csv').concat(new QueryString(params).toString(true))),
    getTrackedHoursPerWorkspace: (params = {}) =>
      get(reportsUrl.concat('/tracked-hours-per-workspace/csv').concat(new QueryString(params).toString(true))),
    getTrialsEndingSoon: (params = {}) =>
      get(reportsUrl.concat('/trials-ending-soon/csv').concat(new QueryString(params).toString(true))),
    getWeeklyActiveUsers: (params = {}) =>
      get(reportsUrl.concat('/weekly-active-users/csv').concat(new QueryString(params).toString(true))),
  },
  feedbackItems: {
    get: (params = {}) => get(feedbackItemsUrl.concat(new QueryString(params).toString(true))),
    export: (params = {}) => get(feedbackItemsUrl.concat('/csv').concat(new QueryString(params).toString(true))),
    sendEmail: (feedbackItemId, body) => post(feedbackItemsUrl.concat(`/${feedbackItemId}/send-email`), body),
  },
  productUpdates: {
    get: (params = {}) => get(productUpdatesUrl.concat(new QueryString(params).toString(true))),
    create: (body) => post(productUpdatesUrl, body),
    update: (productUpdateId, body) => patch(`${productUpdatesUrl}/${productUpdateId}`, body),
    delete: (productUpdateId) => del(`${productUpdatesUrl}/${productUpdateId}`),
    setImage: (productUpdateId, file) =>
      put(
        `${productUpdatesUrl}/${productUpdateId}/image`,
        file?.bytes,
        file ? { headers: { 'Content-Type': file?.type } } : undefined,
      ),
  },
  xero: {
    getIntegrations: () => get(xeroUrl.concat('/integrations')),
    getConnections: (params = {}) => get(xeroUrl.concat('/connections').concat(new QueryString(params).toString(true))),
    removeConnection: (id, params = {}) =>
      del(xeroUrl.concat(`/connections/${id}`).concat(new QueryString(params).toString(true))),
    getHistory: (params = {}) => get(xeroUrl.concat('/history').concat(new QueryString(params).toString(true))),
  },
};
